import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import './BoxPlot.css';

const BoxPlot = ({ data, title, xAxisLabel, yAxisLabel, metric }) => {
  const d3Container = useRef(null);
  
  // Process data for box plot visualization
  const processData = () => {
    return data.map(item => {
      // For each club, calculate the statistical values
      const values = item.shots.map(shot => shot[metric]);
      values.sort((a, b) => a - b);
      
      const min = Math.min(...values);
      const max = Math.max(...values);
      
      // Calculate quartiles
      const q1 = d3.quantile(values, 0.25);
      const median = d3.quantile(values, 0.5);
      const q3 = d3.quantile(values, 0.75);
      const mean = d3.mean(values);
      
      // Calculate IQR and outlier boundaries
      const iqr = q3 - q1;
      const upperWhisker = Math.min(max, q3 + 1.5 * iqr);
      const lowerWhisker = Math.max(min, q1 - 1.5 * iqr);
      
      // Identify outliers
      const outliers = values.filter(
        v => v < lowerWhisker || v > upperWhisker
      );
      
      return {
        name: item.club,
        min,
        max,
        q1,
        median,
        q3,
        mean,
        upperWhisker,
        lowerWhisker,
        outliers,
        count: values.length
      };
    });
  };

  useEffect(() => {
    if (data && d3Container.current) {
      const processedData = processData();
      
      // Clear any existing SVG
      d3.select(d3Container.current).selectAll("*").remove();
      
      // Set dimensions and margins
      const margin = { top: 40, right: 30, bottom: 60, left: 60 };
      const width = d3Container.current.clientWidth - margin.left - margin.right;
      const height = 400 - margin.top - margin.bottom;
      
      // Create SVG
      const svg = d3.select(d3Container.current)
        .append("svg")
        .attr("width", width + margin.left + margin.right)
        .attr("height", height + margin.top + margin.bottom)
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);
      
      // Add title
      svg.append("text")
        .attr("x", width / 2)
        .attr("y", -margin.top / 2)
        .attr("text-anchor", "middle")
        .style("font-size", "16px")
        .style("font-weight", "bold")
        .text(title);
      
      // X scale
      const x = d3.scaleBand()
        .domain(processedData.map(d => d.name))
        .range([0, width])
        .padding(0.4);
      
      // Y scale
      const y = d3.scaleLinear()
        .domain([
          d3.min(processedData, d => Math.min(d.min, ...d.outliers)) * 0.95,
          d3.max(processedData, d => Math.max(d.max, ...d.outliers)) * 1.05
        ])
        .range([height, 0]);
      
      // Add X axis
      svg.append("g")
        .attr("transform", `translate(0,${height})`)
        .call(d3.axisBottom(x))
        .selectAll("text")
        .style("text-anchor", "middle");
      
      // Add X axis label
      svg.append("text")
        .attr("x", width / 2)
        .attr("y", height + margin.bottom - 10)
        .style("text-anchor", "middle")
        .text(xAxisLabel);
      
      // Add Y axis
      svg.append("g")
        .call(d3.axisLeft(y));
      
      // Add Y axis label
      svg.append("text")
        .attr("transform", "rotate(-90)")
        .attr("y", -margin.left + 15)
        .attr("x", -height / 2)
        .attr("text-anchor", "middle")
        .text(yAxisLabel);
      
      // Add grid lines
      svg.append("g")
        .attr("class", "grid")
        .call(d3.axisLeft(y)
          .tickSize(-width)
          .tickFormat("")
        )
        .selectAll("line")
        .style("stroke", "#e0e0e0")
        .style("stroke-opacity", "0.7")
        .style("shape-rendering", "crispEdges");
      
      // Create a group for each box plot
      const boxWidth = x.bandwidth();
      
      const boxGroups = svg.selectAll(".box")
        .data(processedData)
        .enter()
        .append("g")
        .attr("transform", d => `translate(${x(d.name)},0)`);
      
      // Draw the boxes
      boxGroups.append("rect")
        .attr("class", "box")
        .attr("x", 0)
        .attr("y", d => y(d.q3))
        .attr("width", boxWidth)
        .attr("height", d => y(d.q1) - y(d.q3))
        .attr("fill", "#8884d8")
        .attr("stroke", "black")
        .attr("stroke-width", 1)
        .style("opacity", 0.8);
      
      // Draw median lines
      boxGroups.append("line")
        .attr("class", "median-line")
        .attr("x1", 0)
        .attr("x2", boxWidth)
        .attr("y1", d => y(d.median))
        .attr("y2", d => y(d.median))
        .attr("stroke", "black")
        .attr("stroke-width", 2);
      
      // Draw whiskers (vertical lines)
      boxGroups.append("line")
        .attr("class", "whisker-line")
        .attr("x1", boxWidth / 2)
        .attr("x2", boxWidth / 2)
        .attr("y1", d => y(d.lowerWhisker))
        .attr("y2", d => y(d.q1))
        .attr("stroke", "black")
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "3,3");
      
      boxGroups.append("line")
        .attr("class", "whisker-line")
        .attr("x1", boxWidth / 2)
        .attr("x2", boxWidth / 2)
        .attr("y1", d => y(d.q3))
        .attr("y2", d => y(d.upperWhisker))
        .attr("stroke", "black")
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "3,3");
      
      // Draw whisker caps (horizontal lines)
      boxGroups.append("line")
        .attr("class", "whisker-cap")
        .attr("x1", boxWidth * 0.25)
        .attr("x2", boxWidth * 0.75)
        .attr("y1", d => y(d.lowerWhisker))
        .attr("y2", d => y(d.lowerWhisker))
        .attr("stroke", "black")
        .attr("stroke-width", 1);
      
      boxGroups.append("line")
        .attr("class", "whisker-cap")
        .attr("x1", boxWidth * 0.25)
        .attr("x2", boxWidth * 0.75)
        .attr("y1", d => y(d.upperWhisker))
        .attr("y2", d => y(d.upperWhisker))
        .attr("stroke", "black")
        .attr("stroke-width", 1);
      
      // Draw outliers
      boxGroups.each(function(d) {
        d3.select(this).selectAll(".outlier")
          .data(d.outliers)
          .enter()
          .append("circle")
          .attr("class", "outlier")
          .attr("cx", boxWidth / 2)
          .attr("cy", value => y(value))
          .attr("r", 3)
          .attr("fill", "red")
          .attr("stroke", "black")
          .attr("stroke-width", 1);
      });
      
      // Draw mean points
      boxGroups.append("circle")
        .attr("class", "mean-point")
        .attr("cx", boxWidth / 2)
        .attr("cy", d => y(d.mean))
        .attr("r", 4)
        .attr("fill", "#ff7300")
        .attr("stroke", "black")
        .attr("stroke-width", 1);
      
      // Add tooltips
      const tooltip = d3.select(d3Container.current)
        .append("div")
        .attr("class", "boxplot-tooltip")
        .style("opacity", 0)
        .style("position", "absolute")
        .style("pointer-events", "none");
      
      boxGroups.on("mouseover", function(event, d) {
        tooltip.transition()
          .duration(200)
          .style("opacity", 0.9);
        
        tooltip.html(`
          <p class="tooltip-club">${d.name}</p>
          <p>Min: ${d.min.toFixed(1)}</p>
          <p>Q1: ${d.q1.toFixed(1)}</p>
          <p>Median: ${d.median.toFixed(1)}</p>
          <p>Q3: ${d.q3.toFixed(1)}</p>
          <p>Max: ${d.max.toFixed(1)}</p>
          <p>Mean: ${d.mean.toFixed(1)}</p>
          <p>Shots: ${d.count}</p>
        `)
          .style("left", (event.pageX + 10) + "px")
          .style("top", (event.pageY - 28) + "px");
      })
      .on("mouseout", function() {
        tooltip.transition()
          .duration(500)
          .style("opacity", 0);
      });
      
      // Add legend
      const legend = svg.append("g")
        .attr("class", "legend")
        .attr("transform", `translate(${width - 120}, 0)`);
      
      legend.append("rect")
        .attr("x", 0)
        .attr("y", 0)
        .attr("width", 15)
        .attr("height", 15)
        .attr("fill", "#8884d8");
      
      legend.append("text")
        .attr("x", 20)
        .attr("y", 12)
        .text("IQR Box");
      
      legend.append("line")
        .attr("x1", 0)
        .attr("x2", 15)
        .attr("y1", 30)
        .attr("y2", 30)
        .attr("stroke", "black")
        .attr("stroke-width", 2);
      
      legend.append("text")
        .attr("x", 20)
        .attr("y", 35)
        .text("Median");
      
      legend.append("circle")
        .attr("cx", 7)
        .attr("cy", 50)
        .attr("r", 4)
        .attr("fill", "#ff7300");
      
      legend.append("text")
        .attr("x", 20)
        .attr("y", 55)
        .text("Mean");
      
      legend.append("circle")
        .attr("cx", 7)
        .attr("cy", 70)
        .attr("r", 3)
        .attr("fill", "red");
      
      legend.append("text")
        .attr("x", 20)
        .attr("y", 75)
        .text("Outlier");
    }
  }, [data, metric, title, xAxisLabel, yAxisLabel]);

  return (
    <div className="boxplot-container" ref={d3Container}>
      {/* D3 will render the box plot here */}
    </div>
  );
};

export default BoxPlot;
